import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import {
	AppColorKind,
	AppColorViewModel,
	AppType
} from "client/Api/Apps/common";
import {
	DocumentItemType,
	DocumentItemViewModel,
	DocumentItemViewModelBase,
	Permissions
} from "client/Api/Documents/SharePoint/common";
import { HubItemMediaKind, HubItemMediaPreviewViewModel } from "client/Api/Hub/common";
import { HubItemCategoryListItemModel } from "client/Api/Hub/hub";
import { TagViewModel } from "client/Api/Tags/tags";
import { MaintenanceWindowAppModel } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import {
	AppColorPersistedModel,
	AppGlobalSearchItemPersistedModel,
	AppImagePersistedModel,
	DocumentGlobalSearchItemPersistedModel,
	DocumentPermissionsPersistenceModel,
	HubItemCategoryListItemPersistedModel,
	HubItemMediaPersistedModel,
	MaintenanceWindowPersistedModel,
	TagPersistedModel,
	UserIdentityPersistedModel
} from "./models";
import { AppNavigationParameters } from "client/Features/Layout/TitleBar/GlobalSearch/Navigation/useAppNavigationHandler";
import { isNil } from "client/Misc/utils";
import { logNever } from "client_legacy/ndaw/errors";
import { RemoteDesktopProtocolClientKind } from "client/Api/Apps/EditApp/saveApp";

export function convertHubMedia(persistedMedia: HubItemMediaPersistedModel): HubItemMediaPreviewViewModel {
	const noneMedia: HubItemMediaPreviewViewModel = {
		mediaKind: HubItemMediaKind.None
	};

	const {
		mediaKind,
		image,
		video
	} = persistedMedia;

	switch (mediaKind) {
		case HubItemMediaKind.None:
			return noneMedia;
		case HubItemMediaKind.Image:
			if (!image) {
				console.error("Image is not set on the persisted image model.");
				return noneMedia;
			}

			return {
				mediaKind: HubItemMediaKind.Image,
				imageId: image.imageId,
				imageName: image.imageName,
				focalPoint: {
					left: image.focalPoint.left,
					top: image.focalPoint.top
				}
			};
		case HubItemMediaKind.Video:
			if (!video) {
				console.error("Video is not set on the persisted video model.");
				return noneMedia;
			}

			return {
				mediaKind: HubItemMediaKind.Video,
				videoUrl: video.url
			};
		case HubItemMediaKind.Embed:
			return {
				mediaKind: HubItemMediaKind.Embed
			};
		default:
			logNever(mediaKind);
			return noneMedia;
	}
}

export function convertHubCategory(category: HubItemCategoryListItemPersistedModel): HubItemCategoryListItemModel {
	return {
		id: category.id,
		name: category.name
	};
}

export function convertAppColor(persistedColor: AppColorPersistedModel): AppColorViewModel {
	const defaultColor: AppColorViewModel = {
		kind: AppColorKind.Primary
	};

	switch (persistedColor.kind) {
		case AppColorKind.Header:
		case AppColorKind.Inherit:
		case AppColorKind.Primary:
			return {
				kind: persistedColor.kind
			};
		case AppColorKind.Custom:
			if (!persistedColor.customValue) {
				return defaultColor;
			}

			return {
				kind: AppColorKind.Custom,
				value: persistedColor.customValue
			};
		default:
			logNever(persistedColor.kind);
			return defaultColor;
	}
}

export function convertAppImage(persistedImage: AppImagePersistedModel): AppImageDisplayInfo {
	return {
		imageUrl: persistedImage.imageUrl,
		renderType: persistedImage.renderType
	};
}

export function convertMaintenanceWindow(persistedMaintenanceWindow: MaintenanceWindowPersistedModel): MaintenanceWindowAppModel | null {
	const now = new Date();

	const parsedEndDate: Date = parseDtoDate(persistedMaintenanceWindow.endsOn);

	if (parsedEndDate < now) {
		return null;
	}

	return {
		endsOn: parsedEndDate,
		notificationMessage: persistedMaintenanceWindow.notificationMessage
	};
}

export function convertTag(persistedTag: TagPersistedModel): TagViewModel {
	return {
		id: persistedTag.id,
		name: persistedTag.name
	};
}

export function convertUserIdentityModel(persistedUserIdentity: UserIdentityPersistedModel): UserIdentityModel {
	return {
		avatarId: persistedUserIdentity.avatarId,
		department: persistedUserIdentity.department,
		firstName: persistedUserIdentity.firstName,
		jobTitle: persistedUserIdentity.jobTitle,
		lastName: persistedUserIdentity.lastName,
		officeLocation: persistedUserIdentity.officeLocation,
		pronouns: persistedUserIdentity.pronouns,
		status: persistedUserIdentity.status,
		userId: persistedUserIdentity.userId
	};
}

export function convertDocumentResultToDocumentViewModel(
	documentModel: DocumentGlobalSearchItemPersistedModel
): TryResult<DocumentItemViewModel> {
	const baseModel: DocumentItemViewModelBase = {
		id: documentModel.id,
		bookmarkId: documentModel.bookmarkId,
		createdBy: documentModel.createdBy,
		createdOn: documentModel.createdOn,
		extension: documentModel.extension,
		itemType: documentModel.itemType,
		location: documentModel.location,
		modifiedBy: documentModel.modifiedBy,
		modifiedOn: documentModel.modifiedOn,
		name: documentModel.name,
		parentFolderId: documentModel.parentFolderId,
		permissions: convertDocumentPermissions(documentModel.permissions),
		serverRelativeUrl: documentModel.serverRelativeUrl,
		viewId: documentModel.viewId
	};

	switch (documentModel.itemType) {
		case DocumentItemType.Folder:
		case DocumentItemType.OneNoteNotebook:
			return {
				wasSuccessful: true,
				result: {
					...baseModel,
					itemType: documentModel.itemType
				}
			};
		case DocumentItemType.File:
			if (!documentModel.file) {
				return {
					wasSuccessful: false
				};
			}
			return {
				wasSuccessful: true,
				result: {
					...baseModel,
					itemType: DocumentItemType.File,
					size: documentModel.file.size,
					checkInComment: documentModel.file.checkInComment,
					labels: documentModel.file.labels,
					labelSetting: documentModel.file.labelSetting,
					labelAppliedDateTime: documentModel.file.labelAppliedDateTime,
					labelAppliedBy: documentModel.file.labelAppliedBy,
					isInfected: documentModel.file.isInfected
				}
			};
		default:
			logNever(documentModel.itemType);
			return {
				wasSuccessful: false
			};
	}
}

export function tryConvertAppToNavigationParameters(app: AppGlobalSearchItemPersistedModel): TryResult<AppNavigationParameters> {
	switch (app.appType) {
		case AppType.Office365Word:
		case AppType.Office365Excel:
		case AppType.Office365PowerPoint:
		case AppType.Office365OneNote:
		case AppType.Office365LyncMeeting:
		case AppType.Office365PeopleScreen:
		case AppType.CitrixXenApp:
		case AppType.Yammer:
		case AppType.Embed:
		case AppType.AddressBook:
		case AppType.Hub:
		case AppType.LocalApp:
			return {
				wasSuccessful: true,
				result: {
					appType: app.appType
				}
			};
		case AppType.Autotask:
		case AppType.AzureApp:
		case AppType.Documents:
		case AppType.TopDesk:
		case AppType.Url:
		case AppType.VilansKick:
		case AppType.ConnectWise:
		case AppType.Google:
		case AppType.SharePoint:
		case AppType.Teams:
		case AppType.ToDo:
		case AppType.Liquit:
		case AppType.Magister:
		case AppType.NedapOns:
		case AppType.Afas:
		case AppType.VmwareHorizonApp:
		case AppType.Zaaksysteem:
		case AppType.Zermelo:
		case AppType.ZenyaDoc:
			if (isNil(app.destinationWindow)) {
				return {
					wasSuccessful: false
				};
			}

			return {
				wasSuccessful: true,
				result: {
					appType: app.appType,
					parameters: {
						destinationWindow: app.destinationWindow
					}
				}
			};
		case AppType.Email:
			if (isNil(app.destinationWindow) || isNil(app.emailDestinationApp)) {
				return {
					wasSuccessful: false
				};
			}

			return {
				wasSuccessful: true,
				result: {
					appType: AppType.Email,
					parameters: {
						destinationWindow: app.destinationWindow,
						destinationApp: app.emailDestinationApp
					}
				}
			};
		case AppType.Office365Calendar:
			if (isNil(app.destinationWindow) || isNil(app.calendarDestinationApp)) {
				return {
					wasSuccessful: false
				};
			}

			return {
				wasSuccessful: true,
				result: {
					appType: AppType.Office365Calendar,
					parameters: {
						destinationApp: app.calendarDestinationApp,
						destinationWindow: app.destinationWindow
					}
				}
			};
		case AppType.ClientlessRdp:
			if (isNil(app.clientlessRdpDestinationWindow)) {
				return {
					wasSuccessful: false
				};
			}
			return {
				wasSuccessful: true,
				result: {
					appType: AppType.ClientlessRdp,
					parameters: {
						destinationWindow: app.clientlessRdpDestinationWindow
					}
				}
			};
		case AppType.WindowsVirtualDesktop:
			return {
				wasSuccessful: true,
				result: {
					appType: AppType.WindowsVirtualDesktop,
					parameters: {
						clientKind: app.windowsVirtualDesktopClient
							?? RemoteDesktopProtocolClientKind.MicrosoftTerminalServicesClient
					}
				}
			};
		case AppType.Birthday:
		case AppType.HowTo:
		case AppType.PowerBi:
		case AppType.Rss:
		case AppType.Unknown:
		case AppType.YouTube:
		case AppType.Integration:
			return { wasSuccessful: false };
		default:
			logNever(app.appType);
			return { wasSuccessful: false };
	}
}

function convertDocumentPermissions(permissions: DocumentPermissionsPersistenceModel): Permissions {
	return {
		canCreate: permissions.canCreate,
		canDelete: permissions.canDelete,
		canDownload: permissions.canDownload,
		canEdit: permissions.canEdit,
		canEnumeratePermissions: permissions.canEnumeratePermissions,
		canManagePermissions: permissions.canManagePermissions,
		canView: permissions.canView
	};
}